import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { AlertCircle, BarChart3, Bell, ChevronDown, DollarSign, FileText, Globe, KeyRound, LogOut, Package, PieChart, ShieldCheck, Target, TrendingUp, X } from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import { supabase } from './lib/supabase';
import { formatDate } from './utils/format';
import { BusinessCaseModule } from './modules/BusinessCase/BusinessCaseModule';
import { InventoryModule } from './modules/Inventory/InventoryModule';
import { ProjectMilestones } from './modules/Deals/ProjectMilestones';
import { SellInModule } from './modules/SellIn/SellInModule';
import { SellOutModule } from './modules/SellOut/SellOutModule';
import { LATAMProjectsPage } from './modules/LATAMProjects/LATAMProjectsPage';
import { HomePage } from './modules/Home/HomePage';
// Sell Out - Marketing is hidden on purpose (decided 2026-06; less fresh data, more
// noise than answers). Module kept intact — uncomment here, in the nav items, the
// state, and the render below to bring it back.
// import { SellOutMarketingModule } from './modules/SellOutMarketing/SellOutMarketingModule';
import { FeedbackModule } from './modules/Feedback/FeedbackModule';
import { AssistantWidget } from './components/Assistant/AssistantWidget';

// Lazy-loaded: the heaviest modules (xlsx parsing / charts) that aren't on the
// initial app path, so they're split into separate chunks fetched on demand.
const ForecastModule = lazy(() => import('./modules/Forecast/ForecastModule').then(m => ({ default: m.ForecastModule })));
const SuperAdminPage = lazy(() => import('./modules/Admin/SuperAdminPage').then(m => ({ default: m.SuperAdminPage })));
const DigitalModule = lazy(() => import('./modules/Digital/DigitalModule').then(m => ({ default: m.DigitalModule })));

const Dashboard = () => {
  const { session, profile, loading: authLoading, isRecovery, signIn, signOut, sendPasswordReset, updatePassword, clearRecovery } = useAuth();

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [remember, setRemember] = useState(true);
  const [showForgotScreen, setShowForgotScreen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [recoverySuccess, setRecoverySuccess] = useState(false);
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');

  // In-app "Change Password" modal (for users to replace their temporary password)
  const [showChangePw, setShowChangePw] = useState(false);
  const [cpNew, setCpNew] = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [cpError, setCpError] = useState('');
  const [cpBusy, setCpBusy] = useState(false);
  const [cpSuccess, setCpSuccess] = useState(false);

  const [activeModule, setActiveModuleRaw] = useState(() => {
    const stored = localStorage.getItem('luye_active_module');
    // 'sellout-mkt' is hidden — land those users on Home instead of a blank page.
    return !stored || stored === 'sellout-mkt' ? 'home' : stored;
  });
  const setActiveModule = (mod) => { localStorage.setItem('luye_active_module', mod); setActiveModuleRaw(mod); };
  const [projects, setProjects] = useState([]);
  const [personalTasks, setPersonalTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [inventoryData, setInventoryData] = useState([]);
  const [dataFiles, setDataFiles] = useState({ inventory: null, sales: null, deals: null });
  const [salesData, setSalesData] = useState([]);
  const [skuPerformance, setSkuPerformance] = useState([]);
  const [salesConfig, setSalesConfig] = useState({ marketShare: 0, marketShareChange: 0, forecastAccuracy: 0, yoyGrowth: 0, aopFullYear: 0 });
  const [historicalData, setHistoricalData] = useState({ year2026: [], year2025: [], year2024: [] });
  const [forecastData, setForecastData] = useState({});
  const [forecastChanges, setForecastChanges] = useState([]);
  const [forecastHistory, setForecastHistory] = useState([]);
  const [sellInData, setSellInData] = useState({});
  // const [sellOutMktData, setSellOutMktData] = useState({}); // Sell Out - Marketing hidden, see import note
  const [sellOutData, setSellOutData] = useState({});
  const [digitalData, setDigitalData] = useState({});
  const [insightsData, setInsightsData] = useState({});
  // For the AI assistant context only (Deal Tracker + Business Case summaries).
  const [dealsData, setDealsData] = useState({ projects: [], partnerNotes: [] });
  const [businessCases, setBusinessCases] = useState([]);

  const navGroups = [
    {
      label: 'Performance',
      items: [
        { id: 'home', name: 'Overview', icon: Target },
        { id: 'forecast', name: 'Sales & Forecast', icon: TrendingUp },
        { id: 'sellin', name: 'Sell In', icon: BarChart3 },
        { id: 'sellout', name: 'Sell Out', icon: PieChart },
        // { id: 'sellout-mkt', name: 'Sell Out - Marketing', icon: TrendingUp }, // hidden on purpose, see import note
        { id: 'inventory', name: 'Inventory & RSL', icon: Package },
        { id: 'digital', name: 'Digital Strategy', icon: Globe },
      ],
    },
    {
      label: 'Business & Team',
      items: [
        { id: 'deals', name: 'Deal Tracker', icon: FileText },
        { id: 'business', name: 'Business Case', icon: DollarSign },
        { id: 'actions', name: 'LATAM Projects', icon: AlertCircle },
        { id: 'feedback', name: 'Team Feedback', icon: FileText },
      ],
    },
  ];

  // Load ALL data from Supabase — no more localStorage
  useEffect(() => {
    if (!profile) { setIsLoading(false); return; }
    const loadAll = async () => {
      try {
        const [projRes, notifRes, tasksRes, fcDataRes, fcChangesRes, modRes] = await Promise.all([
          supabase.from('latam_projects').select('*, subtasks:latam_subtasks(*), comments:latam_comments(*)').order('created_at'),
          supabase.from('notifications').select('*').eq('to_email', profile.email).order('created_at', { ascending: false }),
          supabase.from('personal_tasks').select('*').order('created_at', { ascending: false }),
          supabase.from('forecast_data').select('*'),
          supabase.from('forecast_changes').select('*').order('created_at', { ascending: false }),
          supabase.from('module_data').select('*'),
        ]);
        if (projRes.data) {
          setProjects(projRes.data.map(p => ({
            id: p.id, name: p.name, description: p.description, country: p.country, partner: p.partner,
            createdBy: p.created_by, createdAt: p.created_at,
            subtasks: (p.subtasks || []).map(s => ({ id: s.id, title: s.title, assignedTo: s.assigned_to, dueDate: s.due_date, comment: s.comment || '', files: s.files || [], status: s.status, approver: s.approver, completedAt: s.completed_at })),
            comments: (p.comments || []).map(c => ({ id: c.id, text: c.text, author: c.author, createdAt: c.created_at, mentions: c.mentions || [], files: c.files || [] }))
          })));
        }
        if (notifRes.data) {
          setNotifications(notifRes.data.map(n => ({ id: n.id, type: n.type, message: n.message, to: n.to_email, read: n.read, createdAt: n.created_at })));
        }
        if (tasksRes.data) {
          setPersonalTasks(tasksRes.data.map(t => ({ id: t.id, title: t.title, comment: t.comment, dueDate: t.due_date, priority: t.priority, files: t.files || [], status: t.status, createdAt: t.created_at })));
        }
        if (fcDataRes.data && fcDataRes.data.length > 0) {
          const fc = {};
          fcDataRes.data.forEach(row => { fc[row.country_id] = row.content; });
          setForecastData(fc);
        }
        if (fcChangesRes.data) {
          setForecastChanges(fcChangesRes.data.filter(c => c.status === 'pending').map(c => ({
            id: c.id, type: c.change_type, country: c.country_id, productId: c.product_id, productName: c.product_name,
            monthIdx: c.month_idx, monthName: c.month_name, month: c.month_idx, currentValue: c.current_value, newValue: c.new_value,
            currentUnits: parseInt(c.current_value) || 0, newUnits: parseInt(c.new_value) || 0,
            reason: c.reason, requestedBy: c.requested_by, requestedAt: c.created_at, status: 'pending'
          })));
          setForecastHistory(fcChangesRes.data.filter(c => c.status !== 'pending').map(c => ({
            id: c.id, type: c.change_type, country: c.country_id, productId: c.product_id, productName: c.product_name,
            monthIdx: c.month_idx, monthName: c.month_name, month: c.month_idx, currentValue: c.current_value, newValue: c.new_value,
            currentUnits: parseInt(c.current_value) || 0, newUnits: parseInt(c.new_value) || 0,
            reason: c.reason, requestedBy: c.requested_by, requestedAt: c.created_at, status: c.status,
            approvedBy: c.reviewed_by, approvedAt: c.reviewed_at, reviewedBy: c.reviewed_by, reviewedAt: c.reviewed_at
          })));
        }
        // Module data (generic key-value store)
        if (modRes.data) {
          const md = {};
          modRes.data.forEach(row => { md[row.key] = row.content; });
          if (md.inventory) setInventoryData(md.inventory);
          if (md.data_files) setDataFiles(md.data_files);
          if (md.sales) setSalesData(md.sales);
          if (md.sku_performance) setSkuPerformance(md.sku_performance);
          if (md.sales_config) setSalesConfig(md.sales_config);
          if (md.historical_data) setHistoricalData(md.historical_data);
          if (md.sellin) setSellInData(md.sellin);
          if (md.sellout) setSellOutData(md.sellout);
          if (md.digital) setDigitalData(md.digital);
          if (md.insights) setInsightsData(md.insights);
          if (md.bc_list) setBusinessCases(md.bc_list);
          // if (md.sellout_mkt) setSellOutMktData(md.sellout_mkt); // Sell Out - Marketing hidden, see import note
        }
      } catch (e) { console.error('Data load error:', e); setLoadError(true); }
      setIsLoading(false);

      // Deal Tracker data is for the AI assistant context only — load it in its own
      // isolated try so a failure here can never break core dashboard data.
      try {
        const [dealProjRes, dealNotesRes] = await Promise.all([
          supabase.from('projects').select('*'),
          supabase.from('partner_notes').select('*'),
        ]);
        if (dealProjRes?.data || dealNotesRes?.data) {
          setDealsData({ projects: dealProjRes?.data || [], partnerNotes: dealNotesRes?.data || [] });
        }
      } catch (e) { console.error('Deal Tracker load (non-critical) failed:', e); }
    };
    loadAll();
  }, [profile?.email]);

  // Stamp "last seen" each time the app opens, so the Users tab shows real
  // last-active time (not the long-lived last sign-in). Fire and forget.
  useEffect(() => {
    if (!profile?.id) return;
    supabase.from('profiles').update({ last_seen_at: new Date().toISOString() }).eq('id', profile.id).then(({ error }) => { if (error) console.error('last_seen update:', error); });
  }, [profile?.id]);

  const reloadForecast = async () => {
    const { data } = await supabase.from('forecast_data').select('*');
    if (data && data.length > 0) {
      const fc = {};
      data.forEach(row => { fc[row.country_id] = row.content; });
      setForecastData(fc);
    } else {
      setForecastData({});
    }
  };

  const handleLogin = async () => {
    setLoginBusy(true);
    setLoginError('');
    setResetMessage('');
    const { error } = await signIn(loginEmail.trim(), loginPassword, remember);
    if (error) setLoginError(error.message || 'Invalid email or password');
    setLoginBusy(false);
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) { setForgotError('Please enter your email address'); return; }
    setForgotBusy(true);
    setForgotError('');
    const { error } = await sendPasswordReset(forgotEmail.trim());
    if (error) setForgotError(error.message);
    else setForgotSent(true);
    setForgotBusy(false);
  };

  const handleBackToLogin = () => {
    setShowForgotScreen(false);
    setForgotEmail('');
    setForgotError('');
    setForgotSent(false);
  };

  const handleSetNewPassword = async () => {
    if (newPassword.length < 6) { setRecoveryError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setRecoveryError('Passwords do not match'); return; }
    setRecoveryBusy(true);
    setRecoveryError('');
    const { error } = await updatePassword(newPassword);
    if (error) { setRecoveryError(error.message); setRecoveryBusy(false); return; }
    setNewPassword('');
    setConfirmPassword('');
    setRecoveryBusy(false);
    setRecoverySuccess(true);
  };

  const handleRecoveryDone = async () => {
    await signOut();
    clearRecovery();
    setRecoverySuccess(false);
  };

  const openChangePw = () => {
    setCpNew(''); setCpConfirm(''); setCpError(''); setCpSuccess(false); setShowChangePw(true);
  };

  const handleChangePassword = async () => {
    if (cpNew.length < 6) { setCpError('Password must be at least 6 characters'); return; }
    if (cpNew !== cpConfirm) { setCpError('Passwords do not match'); return; }
    setCpBusy(true);
    setCpError('');
    const { error } = await updatePassword(cpNew);
    if (error) { setCpError(error.message); setCpBusy(false); return; }
    setCpBusy(false);
    setCpSuccess(true);
    setCpNew(''); setCpConfirm('');
  };

  const myNotifications = notifications.filter(n => n.to === profile?.email && !n.read);
  const myRecentNotifications = notifications.filter(n => n.to === profile?.email);
  const markingReadRef = useRef(false);

  // Opening the bell marks all unread as read (clears the badge) but keeps them
  // visible in the dropdown for this viewing.
  const toggleNotifications = async () => {
    const opening = !showNotifications;
    setShowNotifications(opening);
    if (opening && myNotifications.length > 0 && !markingReadRef.current) {
      markingReadRef.current = true;
      try {
        // Only persist real (DB) ids — optimistic notifications use temp "n_..." ids.
        const unreadIds = myNotifications.filter(n => !String(n.id).startsWith('n_')).map(n => n.id);
        // Functional update so a notification arriving between render and click isn't dropped.
        setNotifications(prev => prev.map(n => (n.to === profile?.email && !n.read) ? { ...n, read: true } : n));
        if (unreadIds.length > 0) {
          const { error } = await supabase.from('notifications').update({ read: true }).in('id', unreadIds);
          if (error) console.error('Failed to mark notifications read:', error);
        }
      } finally {
        markingReadRef.current = false;
      }
    }
  };

  if (authLoading || (session && isLoading && !loadError)) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }

  if (loadError && session) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow p-6 max-w-md text-center">
          <h2 className="font-semibold text-slate-800 mb-2">Connection Error</h2>
          <p className="text-sm text-slate-500 mb-4">Could not load data from the server. Please check your connection and try again.</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">Retry</button>
        </div>
      </div>
    );
  }

  if (isRecovery && session && profile?.is_active !== false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/luye-logo.png" alt="Luye Pharma" className="w-16 h-16 rounded-2xl object-contain mx-auto mb-4" />
            {recoverySuccess ? (
              <>
                <h1 className="text-2xl font-bold text-slate-800">Password Updated</h1>
                <p className="text-slate-500 text-sm mt-1">Your password has been changed successfully.</p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-slate-800">Set New Password</h1>
                <p className="text-slate-500 text-sm mt-1">Choose a new password for your account.</p>
              </>
            )}
          </div>
          {recoverySuccess ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-green-800 font-semibold text-sm">Your password has been updated successfully.</p>
                <p className="text-green-600 text-sm mt-1">Please sign in with your new password.</p>
              </div>
              <button onClick={handleRecoveryDone} className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
                Go to Sign In
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSetNewPassword()} className="w-full px-4 py-3 border border-slate-300 rounded-lg" placeholder="At least 6 characters" autoComplete="new-password" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSetNewPassword()} className="w-full px-4 py-3 border border-slate-300 rounded-lg" placeholder="Repeat your password" autoComplete="new-password" />
              </div>
              {recoveryError && <p className="text-red-600 text-sm">{recoveryError}</p>}
              <button onClick={handleSetNewPassword} disabled={recoveryBusy} className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
                {recoveryBusy ? 'Updating...' : 'Set New Password'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!session || !profile) {
    // Forgot password screen
    if (showForgotScreen) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
            <div className="text-center mb-8">
              <img src="/luye-logo.png" alt="Luye Pharma" className="w-48 object-contain mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-800">Reset Password</h1>
              <p className="text-slate-500 text-sm mt-2">Enter your email address and we'll send you a link to reset your password.</p>
            </div>
            {forgotSent ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-green-800 font-semibold text-sm">Email sent successfully</p>
                  <p className="text-green-600 text-sm mt-1">Check your inbox at <strong>{forgotEmail}</strong> and click the reset link. Also check your spam folder.</p>
                </div>
                <button onClick={handleBackToLogin} className="w-full py-3 bg-slate-100 text-slate-700 rounded-lg font-semibold hover:bg-slate-200">Back to Sign In</button>
              </div>
            ) : (
              <div className="space-y-4">
                <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleForgotPassword()} className="w-full px-4 py-3 border border-slate-300 rounded-lg" placeholder="Email address" autoComplete="email" autoFocus />
                {forgotError && <p className="text-red-600 text-sm">{forgotError}</p>}
                <button onClick={handleForgotPassword} disabled={forgotBusy} className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
                  {forgotBusy ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button onClick={handleBackToLogin} className="w-full text-sm text-slate-500 hover:text-slate-700">Back to Sign In</button>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Login screen
    return (
      <div className="min-h-screen flex">
        {/* Left brand panel (desktop only) */}
        <div className="relative hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-cyan-500 overflow-hidden flex-col justify-between p-12 text-white">
          {/* Decorative background */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <svg className="absolute inset-0 w-full h-full opacity-[0.07]" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="loginGrid" width="44" height="44" patternUnits="userSpaceOnUse">
                  <path d="M44 0H0V44" fill="none" stroke="white" strokeWidth="1" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#loginGrid)" />
            </svg>
            <svg className="absolute inset-0 w-full h-full opacity-[0.12]" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
              <line x1="0" y1="85%" x2="70%" y2="0" stroke="white" strokeWidth="1.5" />
              <line x1="35%" y1="100%" x2="100%" y2="20%" stroke="white" strokeWidth="1.5" />
              <line x1="70%" y1="100%" x2="100%" y2="60%" stroke="white" strokeWidth="1.5" />
            </svg>
            <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-cyan-300/20 blur-3xl" />
            <div className="absolute top-1/3 -right-16 w-72 h-72 rounded-full bg-blue-400/20 blur-3xl" />
          </div>
          {/* Spacer (keeps message vertically centered) */}
          <div />
          {/* Center message */}
          <div className="relative z-10 max-w-md">
            <h1 className="text-4xl font-bold leading-tight mb-4">Welcome back to your LATAM command center</h1>
            <p className="text-white/80 text-lg mb-8">Your regional analytics and performance hub, all in one secure place.</p>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/15"><TrendingUp className="w-5 h-5" /></span>
                <span className="text-white/90">Real-time business visibility</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/15"><BarChart3 className="w-5 h-5" /></span>
                <span className="text-white/90">Multi-country insights at a glance</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/15"><ShieldCheck className="w-5 h-5" /></span>
                <span className="text-white/90">Secure, role-based access</span>
              </li>
            </ul>
          </div>
          {/* Footer */}
          <div className="relative z-10 text-xs text-white/60">
            <div>© {new Date().getFullYear()} Luye Pharma · LATAM Dashboard</div>
            <div className="mt-1">Developed by <a href="mailto:luis.ornelas@45.digital" className="text-white/80 font-medium hover:text-white hover:underline">45.digital</a></div>
          </div>
        </div>

        {/* Right form panel */}
        <div className="flex-1 flex items-center justify-center bg-slate-50 p-4">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <img src="/luye-logo.png" alt="Luye Pharma" className="w-48 object-contain mx-auto mb-4" />
              <p className="text-blue-600 text-sm font-medium">LATAM Dashboard</p>
            </div>
            <div className="space-y-4">
              <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleLogin()} className="w-full px-4 py-3 border border-slate-300 rounded-lg" placeholder="Email" autoComplete="email" />
              <input type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleLogin()} className="w-full px-4 py-3 border border-slate-300 rounded-lg" placeholder="Password" autoComplete="current-password" />
              <label className="flex items-center gap-2 text-sm text-slate-600 select-none cursor-pointer">
                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} className="w-4 h-4 rounded border-slate-300" />
                Remember me on this device
              </label>
              {loginError && <p className="text-red-600 text-sm">{loginError}</p>}
              <button onClick={handleLogin} disabled={loginBusy} className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
                {loginBusy ? 'Signing in...' : 'Sign In'}
              </button>
              <button onClick={() => setShowForgotScreen(true)} className="w-full text-sm text-blue-600 hover:underline">Forgot password?</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile.is_active) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow p-6 max-w-md text-center">
          <h2 className="font-semibold text-slate-800 mb-2">Account inactive</h2>
          <p className="text-sm text-slate-500 mb-4">Contact your administrator to reactivate access.</p>
          <button onClick={signOut} className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm">Sign out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex">
      <div className="w-64 bg-[#0f172a] flex flex-col flex-shrink-0">
        <div className="px-5 py-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <img src="/luye-logo.png" alt="Luye Pharma" className="h-9 object-contain" />
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mt-2">LATAM Workspace</p>
        </div>
        <nav className="flex-1 p-3 overflow-y-auto">
          {navGroups.map(group => (
            <div key={group.label} className="mb-4">
              <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{group.label}</p>
              {group.items.map(m => {
                const active = activeModule === m.id;
                return (
                  <button key={m.id} onClick={() => setActiveModule(m.id)} className={`relative w-full flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-lg mb-0.5 transition-colors ${active ? 'bg-blue-600 text-white shadow-sm shadow-blue-900/40' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'}`}>
                    {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r bg-white/80" />}
                    <m.icon className="w-[18px] h-[18px]" /><span className="text-sm font-medium">{m.name}</span>
                  </button>
                );
              })}
            </div>
          ))}
          {['super_admin', 'admin'].includes(profile.role) && (
            <div className="mb-2">
              <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Administration</p>
              <button onClick={() => setActiveModule('admin')} className={`relative w-full flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-lg transition-colors ${activeModule === 'admin' ? 'bg-purple-600 text-white shadow-sm shadow-purple-900/40' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'}`}>
                {activeModule === 'admin' && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r bg-white/80" />}
                <ShieldCheck className="w-[18px] h-[18px]" /><span className="text-sm font-medium">Admin</span>
              </button>
            </div>
          )}
        </nav>
        <div className="py-3 pl-7 text-left">
          <span className="text-[10px] text-slate-600 select-none">v4.0.0</span>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {(() => {
          const currentModuleName = activeModule === 'admin' ? 'Admin' : (navGroups.flatMap(g => g.items).find(m => m.id === activeModule)?.name || 'Overview');
          const initials = (profile.name || '').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'U';
          const roleLabel = profile.role === 'super_admin' ? 'Super Admin' : profile.role === 'admin' ? 'Admin' : 'User';
          return (
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3.5">
          <div className="flex items-center justify-between gap-3">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm min-w-0">
              <span className="text-slate-400 font-medium hidden sm:inline">LATAM Workspace</span>
              <span className="text-slate-300 hidden sm:inline">/</span>
              <span className="font-semibold text-slate-800 truncate">{currentModuleName}</span>
            </div>
            {/* Right cluster */}
            <div className="flex items-center gap-1 sm:gap-2">
              <div className="relative">
                <button onClick={toggleNotifications} className="relative p-2 hover:bg-slate-100 rounded-lg">
                  <Bell className="w-5 h-5 text-slate-600" />
                  {myNotifications.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">{myNotifications.length}</span>}
                </button>
                {showNotifications && (
                  <div className="absolute right-0 top-12 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-xl border border-slate-200 z-50">
                    <div className="p-3 border-b border-slate-200 font-semibold text-slate-800">Notifications</div>
                    <div className="max-h-64 overflow-y-auto">
                      {myRecentNotifications.length === 0 ? <p className="p-4 text-center text-slate-500 text-sm">No notifications</p> : (
                        myRecentNotifications.map(n => (
                          <div key={n.id} className="p-3 hover:bg-slate-50 border-b border-slate-100">
                            <p className="text-sm text-slate-700">{n.message}</p>
                            <p className="text-xs text-slate-400 mt-1">{formatDate(n.createdAt)}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* User menu */}
              <div className="relative">
                <button onClick={() => setShowUserMenu(v => !v)} className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-slate-100">
                  <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold shrink-0">{initials}</div>
                  <span className="text-sm font-medium text-slate-700 hidden sm:inline max-w-[10rem] truncate">{profile.name}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>
                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 top-12 w-60 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
                      <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold shrink-0">{initials}</div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{profile.name}</p>
                          <span className="inline-block mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{roleLabel}</span>
                        </div>
                      </div>
                      <button onClick={() => { setShowUserMenu(false); openChangePw(); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"><KeyRound className="w-4 h-4 text-slate-400" /> Change password</button>
                      <button onClick={() => { setShowUserMenu(false); signOut(); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"><LogOut className="w-4 h-4" /> Sign out</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>
          );
        })()}

        {showChangePw && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowChangePw(false)}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><KeyRound className="w-5 h-5 text-blue-600" /> Change Password</h3>
                <button onClick={() => setShowChangePw(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
              </div>
              {cpSuccess ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <p className="text-green-800 font-semibold text-sm">Your password has been updated successfully.</p>
                  </div>
                  <button onClick={() => setShowChangePw(false)} className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">Done</button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">Set a new personal password to replace your temporary one.</p>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                    <input type="password" value={cpNew} onChange={e => setCpNew(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleChangePassword()} className="w-full px-4 py-3 border border-slate-300 rounded-lg" placeholder="At least 6 characters" autoComplete="new-password" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password</label>
                    <input type="password" value={cpConfirm} onChange={e => setCpConfirm(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleChangePassword()} className="w-full px-4 py-3 border border-slate-300 rounded-lg" placeholder="Repeat your password" autoComplete="new-password" />
                  </div>
                  {cpError && <p className="text-red-600 text-sm">{cpError}</p>}
                  <button onClick={handleChangePassword} disabled={cpBusy} className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
                    {cpBusy ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        <main className="flex-1 overflow-auto px-6 pt-6 pb-28">
          <Suspense fallback={<div className="flex items-center justify-center py-20"><div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>}>
            {activeModule === 'home' && <HomePage user={profile} projects={projects} setProjects={setProjects} personalTasks={personalTasks} setPersonalTasks={setPersonalTasks} notifications={notifications} setNotifications={setNotifications} setActiveModule={setActiveModule} forecastData={forecastData} sellOutData={sellOutData} inventoryData={inventoryData} insightsData={insightsData} />}
            {activeModule === 'forecast' && <ForecastModule user={profile} forecastData={forecastData} setForecastData={setForecastData} forecastChanges={forecastChanges} setForecastChanges={setForecastChanges} forecastHistory={forecastHistory} setForecastHistory={setForecastHistory} />}
            {activeModule === 'sellin' && <SellInModule sellInData={sellInData} />}
            {activeModule === 'sellout' && <SellOutModule sellOutData={sellOutData} setSellOutData={setSellOutData} />}
            {activeModule === 'digital' && <DigitalModule digitalData={digitalData} />}
            {/* {activeModule === 'sellout-mkt' && <SellOutMarketingModule mktData={sellOutMktData} />} Sell Out - Marketing hidden, see import note */}
            {activeModule === 'feedback' && <FeedbackModule />}
            {activeModule === 'actions' && <LATAMProjectsPage user={profile} projects={projects} setProjects={setProjects} notifications={notifications} setNotifications={setNotifications} />}
            {activeModule === 'inventory' && <InventoryModule inventoryData={inventoryData} />}
            {activeModule === 'deals' && <ProjectMilestones />}
            {activeModule === 'business' && <BusinessCaseModule />}
            {activeModule === 'admin' && <SuperAdminPage setInventoryData={setInventoryData} setSalesData={setSalesData} setSkuPerformance={setSkuPerformance} setSalesConfig={setSalesConfig} setHistoricalData={setHistoricalData} dataFiles={dataFiles} setDataFiles={setDataFiles} forecastData={forecastData} setForecastData={setForecastData} reloadForecast={reloadForecast} sellInData={sellInData} setSellInData={setSellInData} sellOutData={sellOutData} setSellOutData={setSellOutData} setDigitalData={setDigitalData} insightsData={insightsData} setInsightsData={setInsightsData} />}
          </Suspense>
        </main>
      </div>

      {/* AI assistant — live for the whole team (any logged-in user). */}
      <AssistantWidget forecastData={forecastData} sellInData={sellInData} sellOutData={sellOutData} inventoryData={inventoryData} digitalData={digitalData} insightsData={insightsData} projects={projects} deals={dealsData} businessCases={businessCases} />
    </div>
  );
};

export default Dashboard;
